import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { GlobalLayoutProvider, GlobalLayoutContext } from '$contexts/global-layout.jsx';
import { Footer, Header, Meta, Breadcrumb } from '$partials/global';
import Image from '$partials/media/image';
import ModuleDispatcher from '$partials/modules/dispatcher';
import { useLocale } from '$contexts/locale';
import { findModuleAnchors, getAllModules } from '$utilities/data';
import { useAnchor } from '$contexts/anchor';
import AnchorNavigation from '$partials/ui/anchor-navigation';
import ModuleRelatedCarousel from '$partials/modules/individual/related-carousel';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const ResourcePage = ({ data, domain }) => {
    const [stylingWhite, setStylingWhite] = useState(true);

    const { translate: t } = useLocale();
    const { setAnchors, anchors } = useAnchor();
    const page = data?.page;
    let anchorModules = [];
    const modules = getAllModules(page);

    useEffect(() => {
        if (modules) {
            anchorModules = anchorModules.concat(findModuleAnchors(modules));
            setAnchors(anchorModules);
        }
    }, []);

    return (
        <>
            <Meta page={page} />

            <main id='main-content'>
                <div className='min-h-screen'>
                    <GlobalLayoutProvider>
                        <GlobalLayoutContext.Consumer>
                            {(global) => (
                                <>
                                    <Header
                                        menu={data?.navigation}
                                        setStylingWhite={setStylingWhite}
                                        stylingWhite={stylingWhite} />

                                    <div className='layout-container z-10 relative'>

                                        {/* Header */}
                                        <div className='mt-60 md:mt-160'>
                                            <div className='px-10 sm:px-5 md:px-10'>
                                                {page.categories?.items?.length > 0 && (
                                                    <div className='flex items-center mb-20 md:mb-30'>
                                                        {page.categories.items[0]?.icon && (
                                                            <div
                                                                className='mr-10'
                                                                style={{
                                                                    fontSize: '24px',
                                                                    width: `${(page.categories.items[0]?.icon.width / page.categories.items[0]?.icon.height)}em`,
                                                                    maxWidth: '100%'
                                                                }}>
                                                                <Image
                                                                    {...page.categories.items[0]?.icon}
                                                                    loading={''} />
                                                            </div>
                                                        )}
                                                        <p className='text-black type-style-7'>{page.categories.items[0]?.title}</p>
                                                    </div>
                                                )}

                                                <h1 className='type-style-1 mb-30 md:mb-70'>{page?.title}</h1>
                                            </div>
                                        </div>
                                    </div>


                                    {page.heroImage && page.displayHeroImageWhole && (
                                        <figure className='max-w-full'>
                                            <div className='aspect-[5/2]'>
                                                <div className='h-full left-1/2 -translate-x-1/2 relative w-full px-10 '>
                                                    <Image
                                                        {...page.heroImage}
                                                        className='w-full h-full object-cover'
                                                        containerClassName='w-full h-full relative'
                                                        height='100%'
                                                        loadWidth={2560}
                                                        width='100%' />
                                                </div>
                                            </div>
                                        </figure>
                                    )}

                                    {page.heroImage && !page.displayHeroImageWhole && (
                                        <figure className='max-w-full'>
                                            <div className='left-1/2 -translate-x-1/2 relative w-full px-10 '>
                                                <div
                                                    className='min-h-[200px] sm:min-h-[422px] md:min-h-[475px] w-full bg-contain bg-repeat-x'
                                                    style={{ backgroundImage: `url(${page.heroImage?.src})` }}
                                                    title={page.heroImage?.title} />
                                            </div>
                                        </figure>
                                    )}

                                    <div className='pt-55'>
                                        <Breadcrumb
                                            parent={data.parentPage?.title}
                                            parentSlug='resources'
                                            title={page?.title} />
                                    </div>

                                    {/* Content */}
                                    {(modules?.length > 0) && (
                                        <section className={`${anchors.length > 0 ? 'mt-20' : 'mt-40'} sm:mt-50 md:mt-60 relative flex flex-col md:flex-row`}>
                                            {anchors.length > 0 && (
                                                <div className='relative md:absolute h-full w-full pointer-events-none'>
                                                    <div
                                                        className='relative z-10'>
                                                        <AnchorNavigation
                                                            anchors={anchors}
                                                            className='px-10 py-10' />
                                                    </div>
                                                </div>
                                            )}
                                            <div
                                                className='md:-mt-30 w-full'>
                                                {modules.map((module, i) => {
                                                    return (
                                                        <ModuleDispatcher
                                                            key={i}
                                                            module={module}
                                                            nextTypename={modules[i + 1]?.__typename}
                                                            parent={'Detail'}
                                                            previousTypename={modules[i - 1]?.__typename} />
                                                    );
                                                })}
                                            </div>
                                        </section>
                                    )}
                                </>
                            )}
                        </GlobalLayoutContext.Consumer>
                    </GlobalLayoutProvider>
                    {/* Related */}
                    {data.relatedArticles?.items?.length > 0 && (
                        <ModuleRelatedCarousel
                            ctaLabel={t('View All')}
                            ctaLink='/resources'
                            items={data.relatedArticles?.items}
                            parent={'Detail'}
                            title={t('Related Resources')} />
                    )}

                    <div className='mt-60 md:mt-50'>
                        <Footer
                            domain={domain}
                            menu={data?.footer} />
                    </div>
                </div>
            </main>
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

const propTypes = {
    data: PropTypes.shape({
        footer: PropTypes.shape({
            items: PropTypes.array
        }),
        page: PropTypes.object,
        parentPage: PropTypes.shape({
            title: PropTypes.string,
        }),
        relatedArticles: PropTypes.object,
        navigation: PropTypes.object,
    }),
    domain: PropTypes.string,
};

ResourcePage.propTypes = propTypes;

export default ResourcePage;
